/**
 * pc-order-limits.js — storefront Order Limits enforcement
 * (in-house Minmaxify replacement).
 *
 * Limits come from window.__PC_ORDER_LIMITS (injected by the
 * pc-order-limits.liquid snippet from the `paulas-choice-app/order-limits`
 * shop metafield, managed by pcm-api):
 *   [{ id, sku, minQty, maxQty, status }]
 *   • maxQty > 0 → at most that many units of the SKU in the cart
 *   • minQty > 0 → at least that many units IF the SKU is in the cart
 *   • quantities are summed across cart lines with the same SKU
 *
 * What it does:
 *   • re-validates the cart on load, on Impulse cart events and after any
 *     AJAX cart mutation (fetch/XHR to /cart/add|change|update|clear)
 *   • while violated: checkout buttons show "ORDER NOT VALID" and clicking
 *     them opens an explanatory modal instead of navigating
 *   • the checkout itself is hard-gated by the order-limits-ui checkout
 *     extension reading the same metafield — this layer is UX.
 */
(function () {
  'use strict';

  var RAW = window.__PC_ORDER_LIMITS;
  if (!Array.isArray(RAW)) return;

  var LIMITS = {};
  var LIMIT_COUNT = 0;
  RAW.forEach(function (l) {
    if (!l || l.status !== true || !l.sku) return;
    LIMITS[String(l.sku)] = {
      min: l.minQty > 0 ? l.minQty : 0,
      max: l.maxQty > 0 ? l.maxQty : 0,
    };
    LIMIT_COUNT++;
  });
  if (LIMIT_COUNT === 0) return;

  var STRINGS = {
    title: 'Order limit reached',
    intro: 'Please adjust the quantities below to place your order:',
    max: function (v) {
      return 'Maximum of ' + v.max + ' — ' + v.title + ' (you have ' + v.qty + ')';
    },
    min: function (v) {
      return 'Minimum of ' + v.min + ' — ' + v.title + ' (you have ' + v.qty + ')';
    },
    reviewCart: 'Review cart',
    close: 'Close',
    invalidButton: 'ORDER NOT VALID',
  };
  // Allow per-theme/per-locale overrides injected before this script.
  if (window.__PC_ORDER_LIMITS_I18N) {
    Object.keys(window.__PC_ORDER_LIMITS_I18N).forEach(function (k) {
      STRINGS[k] = window.__PC_ORDER_LIMITS_I18N[k];
    });
  }

  var state = {
    violations: [],
    signature: '',
    modalEl: null,
    checking: false,
    disabled: false,
  };

  // Circuit breaker: a healthy session needs a handful of button writes.
  // If we ever blow way past that (a repaint feedback loop with the theme,
  // a future regression, anything), disarm the storefront layer entirely —
  // the order-limits-ui checkout extension still blocks invalid orders.
  var writeCount = 0;
  var WRITE_BREAKER = 200;
  function countWrite() {
    writeCount++;
    if (writeCount > WRITE_BREAKER && !state.disabled) {
      state.disabled = true;
      state.violations = [];
      console.error('[pc-order-limits] write breaker tripped — storefront layer disabled (checkout extension still enforces limits)');
    }
    return !state.disabled;
  }

  /* ── validation ────────────────────────────────────────────────────── */

  function validate(cart) {
    var perSku = {};
    (cart.items || []).forEach(function (item) {
      if (!item.sku) return;
      if (!perSku[item.sku]) {
        perSku[item.sku] = { qty: 0, title: item.product_title || item.title || item.sku };
      }
      perSku[item.sku].qty += item.quantity;
    });

    var violations = [];
    Object.keys(LIMITS).forEach(function (sku) {
      var inCart = perSku[sku];
      if (!inCart || inCart.qty === 0) return; // limits apply only when the SKU is in the cart
      var limit = LIMITS[sku];
      if (limit.max > 0 && inCart.qty > limit.max) {
        violations.push({ sku: sku, type: 'max', max: limit.max, qty: inCart.qty, title: inCart.title });
      } else if (limit.min > 0 && inCart.qty < limit.min) {
        violations.push({ sku: sku, type: 'min', min: limit.min, qty: inCart.qty, title: inCart.title });
      }
    });
    return violations;
  }

  function fetchCart() {
    return fetch('/cart.js', { credentials: 'same-origin' }).then(function (res) {
      return res.json();
    });
  }

  function refresh(showModalOnNewViolation) {
    if (state.checking) return;
    state.checking = true;
    fetchCart()
      .then(function (cart) {
        state.violations = validate(cart);
        var signature = state.violations
          .map(function (v) { return v.sku + ':' + v.type + ':' + v.qty; })
          .sort()
          .join('|');
        var isNew = signature !== state.signature && state.violations.length > 0;
        state.signature = signature;
        paintButtons();
        if (showModalOnNewViolation && isNew) openModal();
      })
      .catch(function () { /* network hiccup — keep previous state */ })
      .then(function () {
        state.checking = false;
      });
  }

  /* ── checkout buttons ──────────────────────────────────────────────── */

  function checkoutButtons() {
    return Array.prototype.slice.call(
      document.querySelectorAll('button[name="checkout"], input[name="checkout"]')
    );
  }

  // IMPORTANT: must be idempotent — a MutationObserver re-runs this on DOM
  // changes, and writing textContent is itself a DOM change. Only touch the
  // DOM when the value actually differs, or the observer loops forever.
  function paintButtons() {
    if (state.disabled) return;
    checkoutButtons().forEach(function (btn) {
      var isInput = btn.tagName === 'INPUT';
      if (state.violations.length > 0) {
        if (!btn.hasAttribute('data-pc-ol-original')) {
          btn.setAttribute('data-pc-ol-original', isInput ? btn.value : btn.textContent);
        }
        if (isInput) {
          if (btn.value !== STRINGS.invalidButton && countWrite()) btn.value = STRINGS.invalidButton;
        } else if (btn.textContent !== STRINGS.invalidButton && countWrite()) {
          btn.textContent = STRINGS.invalidButton;
        }
        if (!btn.classList.contains('pc-ol-invalid')) btn.classList.add('pc-ol-invalid');
      } else if (btn.hasAttribute('data-pc-ol-original')) {
        var original = btn.getAttribute('data-pc-ol-original');
        if (isInput) {
          if (btn.value !== original) btn.value = original;
        } else if (btn.textContent !== original) {
          btn.textContent = original;
        }
        btn.removeAttribute('data-pc-ol-original');
        btn.classList.remove('pc-ol-invalid');
      }
    });
  }

  // Capture-phase gate: block checkout navigation while violated.
  // FAIL-OPEN by design: the modal is opened BEFORE preventDefault, so if
  // anything in our code throws, the click proceeds normally and the
  // order-limits-ui checkout extension (the hard gate) still blocks invalid
  // orders. A storefront-script failure must never trap the customer.
  document.addEventListener(
    'click',
    function (event) {
      try {
        var target = event.target && event.target.closest
          ? event.target.closest('button[name="checkout"], input[name="checkout"], a[href$="/checkout"], a[href*="/checkout?"]')
          : null;
        if (!target) return;
        if (state.violations.length > 0) {
          openModal();
          event.preventDefault();
          event.stopPropagation();
        }
        // No violations → let the theme handle the click normally.
      } catch (error) {
        console.error('[pc-order-limits] click gate failed open:', error);
      }
    },
    true
  );

  /* ── modal ─────────────────────────────────────────────────────────── */

  var CSS =
    '.pc-ol-overlay{position:fixed;inset:0;z-index:99999;display:flex;align-items:center;justify-content:center;padding:20px;background:rgba(17,17,17,.55);opacity:0;transition:opacity .18s ease}' +
    // CRITICAL: [hidden] loses to the display:flex above by default, which
    // would leave an invisible full-screen overlay swallowing every click.
    '.pc-ol-overlay[hidden]{display:none!important;pointer-events:none!important}' +
    '.pc-ol-overlay.is-open{opacity:1}' +
    '.pc-ol-modal{background:#fff;max-width:440px;width:100%;border-radius:10px;box-shadow:0 18px 50px rgba(0,0,0,.28);padding:28px;transform:translateY(8px);transition:transform .18s ease;font-family:inherit;color:#111}' +
    '.pc-ol-overlay.is-open .pc-ol-modal{transform:translateY(0)}' +
    '.pc-ol-modal__icon{width:44px;height:44px;border-radius:50%;background:#fdecea;color:#c0362c;display:flex;align-items:center;justify-content:center;margin:0 auto 14px;font-size:22px;line-height:1}' +
    '.pc-ol-modal__title{font-size:18px;font-weight:600;text-align:center;margin:0 0 6px;letter-spacing:.02em}' +
    '.pc-ol-modal__intro{font-size:13px;text-align:center;color:#555;margin:0 0 16px}' +
    '.pc-ol-modal__list{list-style:none;margin:0 0 20px;padding:0;border:1px solid #eee;border-radius:8px;overflow:hidden}' +
    '.pc-ol-modal__list li{display:flex;gap:10px;align-items:baseline;padding:10px 14px;font-size:13px;line-height:1.45;border-bottom:1px solid #eee}' +
    '.pc-ol-modal__list li:last-child{border-bottom:none}' +
    '.pc-ol-modal__bullet{color:#c0362c;font-weight:700;flex:none}' +
    '.pc-ol-modal__actions{display:flex;gap:10px;justify-content:center}' +
    '.pc-ol-btn{appearance:none;border:none;cursor:pointer;font-size:13px;font-weight:600;letter-spacing:.08em;text-transform:uppercase;padding:12px 22px;border-radius:6px;text-decoration:none;display:inline-block;text-align:center}' +
    '.pc-ol-btn--primary{background:#111;color:#fff}' +
    '.pc-ol-btn--primary:hover{background:#333;color:#fff}' +
    '.pc-ol-btn--ghost{background:transparent;color:#111;border:1px solid #ccc}' +
    '.pc-ol-btn--ghost:hover{border-color:#111}' +
    'button[name="checkout"].pc-ol-invalid,input[name="checkout"].pc-ol-invalid{opacity:.85;cursor:not-allowed}';

  function ensureModal() {
    if (state.modalEl) return state.modalEl;

    var style = document.createElement('style');
    style.textContent = CSS;
    document.head.appendChild(style);

    var overlay = document.createElement('div');
    overlay.className = 'pc-ol-overlay';
    overlay.setAttribute('hidden', '');
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-labelledby', 'pc-ol-title');
    overlay.innerHTML =
      '<div class="pc-ol-modal">' +
      '<div class="pc-ol-modal__icon" aria-hidden="true">!</div>' +
      '<h2 class="pc-ol-modal__title" id="pc-ol-title"></h2>' +
      '<p class="pc-ol-modal__intro"></p>' +
      '<ul class="pc-ol-modal__list"></ul>' +
      '<div class="pc-ol-modal__actions">' +
      '<a class="pc-ol-btn pc-ol-btn--primary" href="/cart"></a>' +
      '<button type="button" class="pc-ol-btn pc-ol-btn--ghost"></button>' +
      '</div>' +
      '</div>';

    overlay.querySelector('.pc-ol-modal__title').textContent = STRINGS.title;
    overlay.querySelector('.pc-ol-modal__intro').textContent = STRINGS.intro;
    overlay.querySelector('.pc-ol-btn--primary').textContent = STRINGS.reviewCart;
    overlay.querySelector('.pc-ol-btn--ghost').textContent = STRINGS.close;

    overlay.addEventListener('click', function (event) {
      if (event.target === overlay) closeModal();
    });
    overlay.querySelector('.pc-ol-btn--ghost').addEventListener('click', closeModal);
    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') closeModal();
    });

    document.body.appendChild(overlay);
    state.modalEl = overlay;
    return overlay;
  }

  function openModal() {
    if (state.violations.length === 0) return;
    var overlay = ensureModal();
    overlay.style.display = ''; // undo closeModal's inline hide
    overlay.style.pointerEvents = '';
    var list = overlay.querySelector('.pc-ol-modal__list');
    list.innerHTML = '';
    state.violations.forEach(function (v) {
      var li = document.createElement('li');
      var bullet = document.createElement('span');
      bullet.className = 'pc-ol-modal__bullet';
      bullet.textContent = '•';
      var text = document.createElement('span');
      text.textContent = v.type === 'max' ? STRINGS.max(v) : STRINGS.min(v);
      li.appendChild(bullet);
      li.appendChild(text);
      list.appendChild(li);
    });
    // Hide the "Review cart" link when we're already on the cart page.
    var reviewBtn = overlay.querySelector('.pc-ol-btn--primary');
    reviewBtn.style.display = window.location.pathname === '/cart' ? 'none' : '';
    overlay.removeAttribute('hidden');
    requestAnimationFrame(function () {
      overlay.classList.add('is-open');
    });
  }

  function closeModal() {
    if (!state.modalEl) return;
    state.modalEl.classList.remove('is-open');
    var el = state.modalEl;
    // Belt and braces: kill pointer events immediately so the fading overlay
    // can never trap a click, then fully hide it (see [hidden] CSS rule).
    el.style.pointerEvents = 'none';
    setTimeout(function () {
      el.setAttribute('hidden', '');
      el.style.display = 'none';
    }, 180);
  }

  /* ── cart-change detection ─────────────────────────────────────────── */

  var CART_MUTATION = /\/cart\/(add|change|update|clear)(\.js)?/;

  // Impulse dispatches these on ajax cart operations.
  ['cart:updated', 'cart:build-complete', 'ajaxProduct:added'].forEach(function (name) {
    document.addEventListener(name, function () {
      refresh(true);
    });
  });

  // Safety net for cart mutations that bypass theme events.
  var origFetch = window.fetch;
  window.fetch = function () {
    var url = typeof arguments[0] === 'string' ? arguments[0] : (arguments[0] && arguments[0].url) || '';
    var result = origFetch.apply(this, arguments);
    if (CART_MUTATION.test(url)) {
      result.then(function () {
        refresh(true);
      }).catch(function () {});
    }
    return result;
  };

  var origOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url) {
    if (typeof url === 'string' && CART_MUTATION.test(url)) {
      this.addEventListener('loadend', function () {
        refresh(true);
      });
    }
    return origOpen.apply(this, arguments);
  };

  /* ── boot ──────────────────────────────────────────────────────────── */

  function boot() {
    refresh(false);
    // Sections (cart drawer) render after load — repaint when DOM settles.
    // Debounced to one paint per frame; paintButtons is idempotent, so the
    // paint itself produces no further mutations once buttons are correct.
    var rafPending = false;
    var observer = new MutationObserver(function () {
      if (state.violations.length === 0 || rafPending) return;
      rafPending = true;
      requestAnimationFrame(function () {
        rafPending = false;
        try {
          paintButtons();
        } catch (error) {
          observer.disconnect(); // never let a paint error loop or freeze the page
          console.error('[pc-order-limits] repaint disabled after error:', error);
        }
      });
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  try {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', boot);
    } else {
      boot();
    }
  } catch (error) {
    // Fail open: a broken storefront layer must never break the page —
    // the checkout extension still enforces the limits server-side of us.
    console.error('[pc-order-limits] disabled after boot error:', error);
  }
})();
